import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import NotificationBell from "@/components/NotificationBell";
import { BetaFeedbackButton } from "@/components/BetaFeedbackDialog";

export function Logo({ light = false, compact = false }) {
  return (
    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
      <div className="h-8 w-8 rounded-lg bg-[#2563EB] flex items-center justify-center shrink-0">
        <span className="font-heading font-black text-white text-sm">D</span>
      </div>
      {!compact && (
        <div className="leading-none">
          <span className={`font-heading font-extrabold text-[15px] ${light ? "text-white" : "text-[#0A0F1C]"}`}>
            DEI TradeMatch <span className="text-[#2563EB]">AI</span>
          </span>
          <div className={`text-[10px] ${light ? "text-slate-300" : "text-slate-400"}`}>by Debro Enterprise Inc.</div>
        </div>
      )}
    </Link>
  );
}

export default function AppShell({ children, title, subtitle }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebar = (
    <Sidebar
      user={user}
      pathname={location.pathname}
      onNavigate={() => setOpen(false)}
      onLogout={doLogout}
    />
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <aside className="hidden lg:flex flex-col fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200">
        {sidebar}
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="relative w-64 bg-white h-full">{sidebar}</div>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="glass-nav sticky top-0 z-40 border-b border-slate-200/70 px-5 lg:px-10 py-4 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="mobile-menu-btn">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <div className="min-w-0">
            <h1 className="font-heading text-xl sm:text-2xl font-bold text-[#0A0F1C] tracking-tight truncate">{title}</h1>
            {subtitle && <p className="text-sm text-slate-500 truncate">{subtitle}</p>}
          </div>
          {user && <div className="ml-auto flex items-center gap-2"><BetaFeedbackButton /><NotificationBell /></div>}
        </header>
        <main className="px-5 lg:px-10 py-6 lg:py-8 max-w-7xl">{children}</main>
      </div>
    </div>
  );
}
